import {
  RootState,
  INITIAL_STATE,
  Proposal,
} from "../core/state-shape";
import { http } from "@/lib/http";
import { toast } from "sonner";
import { useReviewStore } from "./review";
import useSyncStore from "./sync";
import { measure } from "@/lib/performance";
import { useSettingsStore } from "./settings";
import { createProjectScopedStore } from "./projectScoped";

export type ProposalsState = RootState["proposals"];

type ProposeFixRequest = {
  parentProposalId: string;
  taskId: string;
  filePath: string;
  content: string;
  error: Record<string, any>;
  interactionMode: string;
  llmProviderId?: string | null;
}; 

type ProposalsActions = {
  setState: (newState: ProposalsState) => void;
  patch: (p: Partial<ProposalsState>) => void;
  reset: () => void;
  generate: (taskId: string, contextBundleId: string) => Promise<string>;
  apply: (taskId: string, proposalId: string) => Promise<void>;
  closeProposal: (
    taskId: string,
    proposalId: string
  ) => Promise<{ taskClosed: boolean }>;
  stageProposal: (proposalId: string) => Promise<void>;
  unstageProposal: (proposalId: string) => Promise<void>;
  respondToProposal: (
    taskId: string,
    parentProposalId: string,
    feedback: string
  ) => Promise<{ taskId: string; proposalId: string } | null>;
  finalizeInteractiveSession: (
    proposalId: string
  ) => Promise<{ taskId: string; proposalId: string }>;
  proposeFix: (
    payload: Omit<ProposeFixRequest, "interactionMode" | "llmProviderId">
  ) => Promise<{ taskId: string; proposalId: string; model: string } | null>;
  loadProposalsForTask: (taskId: string) => Promise<void>;
};

export const useProposalsStore = createProjectScopedStore<ProposalsState & ProposalsActions>('proposals',
  (set) => ({
    ...INITIAL_STATE.proposals,
    setState: (newState) => set(newState),
    patch: (p) => set(p),
    reset: () => set({ ...INITIAL_STATE.proposals }),

    generate: measure(async (taskId, contextBundleId) => {
      const res = await http<{
        proposalId: string;
        taskId: string;
        status: string;
        summary: string;
        filesChanged: any[];
        tokensUsed: number;
      }>(`/api/proposals/generate`, {
        method: "POST",
        body: JSON.stringify({ taskId, contextBundleId }),
      });

      set((state) => {
        const byTaskId = { ...state.byTaskId };
        if (!byTaskId[taskId]) {
          byTaskId[taskId] = {};
        }
        byTaskId[taskId][res.proposalId] = {
          id: res.proposalId,
          title: "AI Proposal",
          taskId,
          createdAt: new Date().toISOString(),
          status: "open",
          summary: res.summary,
          filesChanged: res.filesChanged,
          tokensUsed: res.tokensUsed,
        };
        return { byTaskId };
      });

      return res.proposalId;
    }, 'proposals.generate'),

    apply: measure(async (taskId, proposalId) => {
      const { selections } = useReviewStore.getState();

      try {
        await http<void>("/api/proposals/apply", {
          method: "POST",
          body: JSON.stringify({
            taskId,
            proposalId,
            reviewSelections: selections,
          }),
        });
        await useSyncStore.getState().fetchSnapshot(); // Refresh the state from the server after applying
      } catch (e: any) {
        console.error("Failed to apply proposal", e);
        await useSyncStore.getState().fetchSnapshot(); // Fetch to revert to consistent state
        throw e;
      }
    }, 'proposals.apply'),

    stageProposal: measure(async (proposalId: string) => {
      const { selections, editedContent } = useReviewStore.getState();
      try {
        await http<void>(`/api/proposals/${proposalId}/stage`, {
          method: "POST",
          body: JSON.stringify({
            reviewSelections: selections,
            editedContent: editedContent || {},
          }),
        });
      } catch (e: any) {
        throw e;
      }
    }, 'proposals.stageProposal'),

    closeProposal: measure(async (taskId, proposalId): Promise<{ taskClosed: boolean }> => {
      const toastId = toast.loading("Closing proposal...");
      try {
        await http<void>("/api/proposals/close", {
          method: "POST",
          body: JSON.stringify({ taskId, proposalId }),
        });
        await useSyncStore.getState().fetchSnapshot();
        toast.success("Proposal closed.", { id: toastId });
        return {
          taskClosed: Object.values(
            useProposalsStore.getState().byTaskId[taskId] || {}
          ).every((p: any) => p.status === "closed"),
        };
      } catch (e: any) {
        console.error("Failed to close proposal", e);
        toast.error("Failed to close proposal", {
          id: toastId,
          description: e.message,
        });
        await useSyncStore.getState().fetchSnapshot();
        throw e;
      }
    }, 'proposals.closeProposal'),
    finalizeInteractiveSession: measure(async (proposalId: string) => {
      const res = await http<{ taskId: string; proposalId: string }>(
        `/api/proposals/${proposalId}/finalize-interactive-session`,
        {
          method: "POST",
        }
      );
      // Refetch everything to get the updated proposal and task state
      await useSyncStore.getState().fetchSnapshot();
      return res;
    }, 'proposals.finalizeInteractiveSession'),

    unstageProposal: measure(async (proposalId: string) => {
      // This action reverts a proposal from a "staged" state back to its original diff-based state.
      // It's called when entering the review page to ensure a clean slate.
      try {
        // The endpoint simply sets the 'staged_files_changed' column to NULL in the database.
        await http<void>(`/api/proposals/${proposalId}/unstage`, {
          method: "POST",
        });
        await useSyncStore.getState().fetchSnapshot(); // Refresh state to get the clean proposal
      } catch (e: any) {}
    }, 'proposals.unstageProposal'),
    proposeFix: measure(async (payload) => {
      const { interactionMode, llmProviderId } =
        useSettingsStore.getState().workspace;
      const toastId = toast.loading("Requesting AI to propose a fix...");

      try {
        const response = await http<{ taskId: string; proposalId: string }>(
          "/api/proposals/propose-fix",
          {
            method: "POST",
            body: JSON.stringify({
              ...payload,
              interactionMode,
              llmProviderId,
            }),
          }
        );

        await useSyncStore.getState().fetchSnapshot(); // Fetch new state with the cloned proposal
        toast.success("New proposal generated with the fix.", { id: toastId });
        const modelForNav =
          interactionMode === "llm_provider" && llmProviderId
            ? `llm_provider:${llmProviderId}`
            : interactionMode;
        return { ...response, model: modelForNav };
      } catch (e: any) {
        toast.error("Failed to start fix proposal.", {
          id: toastId,
          description: e.message,
        });
        return null;
      }
    }, 'proposals.proposeFix'),
    respondToProposal: measure(async (taskId, parentProposalId, feedback) => {
      const toastId = toast.loading(
        "Sending feedback to generate a new proposal..."
      );
      try {
        const response = await http<{ taskId: string; proposalId: string }>(
          "/api/proposals/respond",
          {
            method: "POST",
            body: JSON.stringify({ taskId, parentProposalId, feedback }),
          }
        );
        await useSyncStore.getState().fetchSnapshot();
        toast.success("New proposal generation started.", { id: toastId });
        return response;
      } catch (e: any) {
        toast.error("Failed to respond to proposal.", {
          id: toastId,
          description: e.message,
        });
        return null;
      }
    }, 'proposals.respondToProposal'),
    loadProposalsForTask: measure(async (taskId: string) => {
      try {
        const res = await http< Proposal[] >(
          `/api/proposals?task_id=${taskId}`
        );
        set((state) => {
          const byTaskId = { ...state.byTaskId };
          const proposalsForTask: Record<string, Proposal> = {};
          if (res) {
            for (const proposal of res) {
              proposalsForTask[proposal.id] = proposal;
            }
          }
          byTaskId[taskId] = { ...(byTaskId[taskId] || {}), ...proposalsForTask };
          console.log('Updating byTaskId for task:', taskId, byTaskId[taskId]);
          return { byTaskId };
        });
      } catch (e: any) {
        toast.error("Failed to load proposals", { description: e.message });
      }
    }, 'proposals.loadProposalsForTask'),
  })
);
